/**
 * The permanent read-only currency badge shown in the app header (2026-08-19).
 *
 * The owner asked for the account's currency to be visible at all times, top
 * right, to channel partners and to end customers — replacing the per-page
 * currency selector that was removed from `/products` the same day.
 *
 * Whose currency, and from where, is the only interesting question here, and the
 * answer differs by who is looking:
 *
 *  - CHANNEL PARTNER → `Account.currency`. Documented as the partner's default
 *    selling currency, set at onboarding, and the server prices their catalog and
 *    quotes from it. A real answer.
 *
 *  - END CUSTOMER → derived from the orders they hold, NOT from their account.
 *    `Account.currency` is `@default("USD")` and the end-customer creation path
 *    never writes it, so every END_CUSTOMER row reads "USD" whether or not that
 *    is true. Printing it would put "Prices in USD" above a CAD order. When their
 *    own records disagree, or there are none yet, the badge is withheld: no
 *    single currency is a true statement about them.
 *
 *  - INTERNAL → nothing. Internal staff transact in no single currency (they work
 *    across USD and CAD partners); the Command Center's currency selector is
 *    their tool for this, and a badge claiming one currency would be wrong more
 *    often than right.
 *
 * WHY THE COPY IS NARROW
 * ----------------------
 * It states the ACCOUNT's currency, not "every amount you see". A CAD partner can
 * hold a USD quote (one does, in local data: $1,000 USD on a CAD account), so the
 * broader claim would be false on the very page that shows it. Individual figures
 * carry their own record's currency already, which is what makes the narrow claim
 * sufficient.
 *
 * Pure, so each rule above is a unit test rather than a claim about a component.
 */

/** The base currency every amount falls back to, matching the server. */
const BASE_CURRENCY = 'USD';

function normalize(value: unknown): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

/** The rendered badge. */
export interface CurrencyBadge {
  code: string;
  label: string;
  title: string;
}

/**
 * The badge for a known currency, or `null` when there is nothing true to say.
 *
 * Callers pass `null` through deliberately — every amount in the portal is
 * already labelled with its own record's currency, so an absent badge loses no
 * information, while a wrong one actively misinforms.
 */
export function currencyBadge(code: string | null | undefined): CurrencyBadge | null {
  const normalized = normalize(code);
  if (!normalized) return null;
  return {
    code: normalized,
    label: `Currency: ${normalized}`,
    title:
      `Your account's currency is ${normalized}. New quotes and orders are created in ` +
      `${normalized}; each existing record shows its own currency.`,
  };
}

/**
 * A channel partner's billing currency, or `null` when we must not claim one.
 *
 * Null for an INTERNAL account (no single currency), for a currency that is not
 * ACTIVE (mirroring the server's fail-soft, so the badge cannot name a currency
 * the app will not price in), and while the account read is still in flight —
 * defaulting to USD there would flash a false claim at a CAD partner on load.
 */
export function partnerAccountCurrency(args: {
  accountType: string | null | undefined;
  accountCurrency: string | null | undefined;
  activeCodes: string[];
}): string | null {
  const accountType = normalize(args.accountType);
  if (!accountType || accountType === 'INTERNAL') return null;

  const currency = normalize(args.accountCurrency);
  if (!currency) return null;

  const active = args.activeCodes.map(normalize);
  return active.includes(currency) ? currency : null;
}

/**
 * The one currency a set of records is all in, or `null` if they are not all in
 * one (or there are none).
 *
 * An absent/blank `currency` counts as the base currency, which is how the server
 * treats a row written before multi-currency existed — so a customer holding only
 * legacy USD orders still gets an accurate badge.
 */
export function soleRecordCurrency(
  records: Array<{ currency?: string | null }> | null | undefined
): string | null {
  if (!records || records.length === 0) return null;
  const distinct = new Set(records.map((r) => normalize(r.currency) || BASE_CURRENCY));
  return distinct.size === 1 ? [...distinct][0] : null;
}
